import React, { useState, useEffect, useContext, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
    Typography,
    Button,
    IconButton,
    Box,
    Stack,
    Fade,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { UserSettingsContext } from "../../../setup/routes-manager/RequireAuth";
import { MANUAL_SECTIONS } from "../../../common/utility/constant";

const FEATURES = [
    { label: "POC", desc: "Point of Control" },
    { label: "VA", desc: "Value Area" },
    { label: "IB", desc: "Initial Balance" },
    { label: "Shape", desc: "Profile structure" },
];

const STEPS = {
    1: {
        anchorId: "vtrender-user-manual-btn",
        placement: "bottom",
        chip: "Step 1 of 2",
        title: "New to Market Profile?",
        body: "Your Market Profile walkthrough lives in the User Manual. Open it to see how profile shape, value area, POC and initial balance are displayed.",
        primaryLabel: "Open User Manual",
        secondaryLabel: "Not now",
    },
    2: {
        anchorId: "manual-section-marketprofile",
        placement: "right",
        chip: "Step 2 of 2",
        title: "Explore Marketprofile",
        body: "You're in the right place. This section walks you through every key element of the Market Profile chart — structure, value area, POC and more.",
        primaryLabel: "Got it",
        secondaryLabel: null,
    },
};

function getAnchorRect(anchorId) {
    const el = document.getElementById(anchorId);
    if (!el) return null;
    return el.getBoundingClientRect();
}

function computePopoverStyle(anchorRect, placement, cardWidth = 380) {
    const gap = 14;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const margin = 16;

    if (placement === "bottom") {
        let left = anchorRect.left + anchorRect.width / 2 - cardWidth / 2;
        left = Math.max(margin, Math.min(left, viewportW - cardWidth - margin));
        const top = anchorRect.bottom + gap;
        const arrowLeft = anchorRect.left + anchorRect.width / 2 - left;
        return { top, left, width: cardWidth, arrowLeft, placement };
    }

    // placement === "right"
    const cardMaxH = 420;
    let top = anchorRect.top + anchorRect.height / 2 - cardMaxH / 2;
    top = Math.max(margin, Math.min(top, viewportH - cardMaxH - margin));
    const left = Math.min(anchorRect.right + gap, viewportW - cardWidth - margin);
    const arrowTop = anchorRect.top + anchorRect.height / 2 - top;
    return { top, left, width: cardWidth, arrowTop, placement };
}

function PopoverArrow({ placement, arrowLeft, arrowTop }) {
    if (placement === "bottom") {
        return (
            <Box
                sx={{
                    position: "absolute",
                    top: -9,
                    left: arrowLeft,
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "10px solid transparent",
                    borderRight: "10px solid transparent",
                    borderBottom: "10px solid #122a52",
                    filter: "drop-shadow(0 -2px 2px rgba(0,0,0,0.15))",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 1,
                        left: -10,
                        width: 0,
                        height: 0,
                        borderLeft: "10px solid transparent",
                        borderRight: "10px solid transparent",
                        borderBottom: "10px solid rgba(99, 140, 255, 0.35)",
                    },
                }}
            />
        );
    }

    return (
        <Box
            sx={{
                position: "absolute",
                left: -9,
                top: arrowTop,
                transform: "translateY(-50%)",
                width: 0,
                height: 0,
                borderTop: "10px solid transparent",
                borderBottom: "10px solid transparent",
                borderRight: "10px solid #122a52",
                filter: "drop-shadow(-2px 0 2px rgba(0,0,0,0.15))",
            }}
        />
    );
}

export default function MarketProfileWalkthroughCoach({
    open,
    onClose,
    onOpenWalkthrough,
}) {
    const [step, setStep] = useState(1);
    const [layout, setLayout] = useState(null);

    const { setShowMPChartWalkthrough, setShowMPSection } =
        useContext(UserSettingsContext);

    const isOpen = Boolean(open);
    const stepConfig = STEPS[step];

    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setLayout(null);
        }
    }, [isOpen]);

    const updateLayout = useCallback(() => {
        if (!isOpen) {
            setLayout(null);
            return;
        }

        const anchorRect = getAnchorRect(stepConfig.anchorId);
        if (!anchorRect) {
            setLayout({ fallback: true });
            return;
        }

        setLayout({
            fallback: false,
            anchorRect,
            popover: computePopoverStyle(anchorRect, stepConfig.placement),
        });
    }, [isOpen, stepConfig.anchorId, stepConfig.placement]);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        const delay = step === 2 ? 450 : 0;
        const timer = setTimeout(updateLayout, delay);

        window.addEventListener("resize", updateLayout);
        window.addEventListener("scroll", updateLayout, true);

        return () => {
            clearTimeout(timer);
            window.removeEventListener("resize", updateLayout);
            window.removeEventListener("scroll", updateLayout, true);
        };
    }, [isOpen, step, updateLayout]);

    const dismiss = () => {
        onClose?.();
        setStep(1);
        setLayout(null);
    };

    const openManualAtMarketProfile = () => {
        const mpIndex = MANUAL_SECTIONS?.findIndex(
            (section) =>
                section.title?.toLowerCase().trim() === "explore marketprofile",
        );

        if (setShowMPSection) {
            setShowMPSection(
                mpIndex !== -1 && mpIndex !== undefined ? mpIndex : 4,
            );
        }

        if (setShowMPChartWalkthrough) {
            setShowMPChartWalkthrough(true);
        }

        if (onOpenWalkthrough) {
            onOpenWalkthrough();
        }
    };

    const handlePrimary = () => {
        if (step === 1) {
            openManualAtMarketProfile();
            setStep(2);
            return;
        }
        dismiss();
    };

    const handleSecondary = () => {
        dismiss();
    };

    if (!isOpen) return null;

    const cardContent = (
        <Box
            sx={{
                position: "relative",
                borderRadius: "16px",
                overflow: "visible",
                background:
                    "linear-gradient(165deg, #122a52 0%, #0b1e3d 55%, #091628 100%)",
                border: "1px solid rgba(99, 140, 255, 0.28)",
                boxShadow:
                    "0 20px 56px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
                color: "#ffffff",
            }}
        >
            <Box
                sx={{
                    height: 4,
                    borderRadius: "16px 16px 0 0",
                    background:
                        "linear-gradient(90deg, #3f69f8 0%, #5b8cff 45%, #4deeea 100%)",
                }}
            />

            {layout && !layout.fallback && (
                <PopoverArrow
                    placement={layout.popover.placement}
                    arrowLeft={layout.popover.arrowLeft}
                    arrowTop={layout.popover.arrowTop}
                />
            )}

            <IconButton
                aria-label="close"
                onClick={dismiss}
                size="small"
                sx={{
                    position: "absolute",
                    right: 10,
                    top: 14,
                    zIndex: 1,
                    color: "rgba(255, 255, 255, 0.55)",
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    "&:hover": {
                        color: "#ffffff",
                        backgroundColor: "rgba(255, 255, 255, 0.12)",
                    },
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ px: 2.75, pt: 2.5, pb: 2.75 }}>
                <Stack
                    direction="row"
                    spacing={1.75}
                    alignItems="flex-start"
                    sx={{ mb: 2, pr: 3.5 }}
                >
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "13px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "linear-gradient(135deg, rgba(63,105,248,0.35) 0%, rgba(77,238,234,0.15) 100%)",
                            border: "1px solid rgba(99, 140, 255, 0.35)",
                        }}
                    >
                        {step === 1 ? (
                            <MenuBookOutlinedIcon
                                sx={{ fontSize: 26, color: "#7aa8ff" }}
                            />
                        ) : (
                            <ShowChartOutlinedIcon
                                sx={{ fontSize: 26, color: "#7aa8ff" }}
                            />
                        )}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Chip
                            icon={
                                <AutoStoriesOutlinedIcon
                                    sx={{
                                        fontSize: "14px !important",
                                        color: "#8eb4ff !important",
                                    }}
                                />
                            }
                            label={stepConfig.chip}
                            size="small"
                            sx={{
                                height: 24,
                                mb: 0.75,
                                color: "#b8ccff",
                                backgroundColor: "rgba(63, 105, 248, 0.14)",
                                border: "1px solid rgba(99, 140, 255, 0.28)",
                                fontWeight: 600,
                                fontSize: "0.68rem",
                                letterSpacing: "0.4px",
                                "& .MuiChip-icon": { ml: "6px" },
                            }}
                        />
                        <Typography
                            id="mp-walkthrough-dialog-title"
                            variant="h6"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                lineHeight: 1.35,
                            }}
                        >
                            {stepConfig.title}
                        </Typography>
                    </Box>
                </Stack>

                <Typography
                    variant="body2"
                    sx={{
                        color: "rgba(224, 232, 255, 0.82)",
                        lineHeight: 1.65,
                        fontSize: "0.88rem",
                        mb: step === 1 ? 2 : 2.5,
                    }}
                >
                    {stepConfig.body}
                </Typography>

                {step === 1 && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 0.75,
                            mb: 2.5,
                        }}
                    >
                        {FEATURES.map((item) => (
                            <Box
                                key={item.label}
                                sx={{
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: "9px",
                                    backgroundColor: "rgba(255, 255, 255, 0.04)",
                                    border: "1px solid rgba(255, 255, 255, 0.07)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 700,
                                        color: "#4deeea",
                                    }}
                                >
                                    {item.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "0.7rem",
                                        color: "rgba(200, 214, 245, 0.65)",
                                    }}
                                >
                                    {item.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                <Stack spacing={1}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handlePrimary}
                        endIcon={
                            step === 1 ? (
                                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                            ) : null
                        }
                        sx={{
                            py: 1.1,
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            background:
                                "linear-gradient(135deg, #3f69f8 0%, #4a7aff 100%)",
                            boxShadow: "0 6px 20px rgba(63, 105, 248, 0.4)",
                            "&:hover": {
                                background:
                                    "linear-gradient(135deg, #355de0 0%, #3f69f8 100%)",
                            },
                        }}
                    >
                        {stepConfig.primaryLabel}
                    </Button>

                    {stepConfig.secondaryLabel && (
                        <Button
                            fullWidth
                            variant="text"
                            onClick={handleSecondary}
                            sx={{
                                py: 0.5,
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "0.84rem",
                                color: "rgba(200, 214, 245, 0.55)",
                                "&:hover": {
                                    color: "rgba(255, 255, 255, 0.85)",
                                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                                },
                            }}
                        >
                            {stepConfig.secondaryLabel}
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );

    return createPortal(
        <Fade in={isOpen} timeout={280}>
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1600,
                    pointerEvents: "none",
                }}
            >
                {/* Dim overlay */}
                <Box
                    onClick={dismiss}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(4, 12, 28, 0.62)",
                        backdropFilter: "blur(2px)",
                        pointerEvents: "auto",
                    }}
                />

                {/* Spotlight ring on target */}
                {layout && !layout.fallback && layout.anchorRect && (
                    <Box
                        sx={{
                            position: "fixed",
                            top: layout.anchorRect.top - 5,
                            left: layout.anchorRect.left - 5,
                            width: layout.anchorRect.width + 10,
                            height: layout.anchorRect.height + 10,
                            borderRadius: "10px",
                            border: "2px solid #4deeea",
                            boxShadow:
                                "0 0 0 4px rgba(77, 238, 234, 0.2), 0 0 28px rgba(63, 105, 248, 0.55)",
                            animation: "mpCoachPulse 2s ease-in-out infinite",
                            pointerEvents: "none",
                            zIndex: 1,
                            "@keyframes mpCoachPulse": {
                                "0%, 100%": {
                                    boxShadow:
                                        "0 0 0 4px rgba(77, 238, 234, 0.2), 0 0 28px rgba(63, 105, 248, 0.55)",
                                },
                                "50%": {
                                    boxShadow:
                                        "0 0 0 8px rgba(77, 238, 234, 0.12), 0 0 36px rgba(63, 105, 248, 0.7)",
                                },
                            },
                        }}
                    />
                )}

                {/* Popover card */}
                <Box
                    sx={{
                        position: "fixed",
                        pointerEvents: "auto",
                        zIndex: 2,
                        ...(layout?.fallback || !layout?.popover
                            ? {
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: "min(380px, calc(100vw - 32px))",
                              }
                            : {
                                  top: layout.popover.top,
                                  left: layout.popover.left,
                                  width: layout.popover.width,
                              }),
                    }}
                >
                    {cardContent}
                </Box>
            </Box>
        </Fade>,
        document.body,
    );
}
